/**
 * Comma-separated labels with suggestions from the existing ones, drawn as an app menu (like the other dropdowns)
 * instead of the browser's own <datalist> popup, which looks different in every browser and can't be styled.
 */
import { useRef, useState } from 'react';
import { useClickOutside } from './components';

export function TagInput({ value, onChange, options, placeholder }: {
  value: string; onChange: (value: string) => void; options: string[]; placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useClickOutside(ref, () => setOpen(false), open);

  const parts = value.split(/[,;]/);
  const typed = parts[parts.length - 1].trim().toLowerCase();
  const chosen = new Set(parts.slice(0, -1).map((p) => p.trim().toLowerCase()).filter(Boolean));
  const matches = options
    .filter((o) => !chosen.has(o.toLowerCase()) && o.toLowerCase().includes(typed))
    // names starting with the typed text first
    .sort((a, b) => Number(!a.toLowerCase().startsWith(typed)) - Number(!b.toLowerCase().startsWith(typed)) || a.localeCompare(b));

  const pick = (name: string) => {
    const kept = parts.slice(0, -1).map((p) => p.trim()).filter(Boolean);
    onChange([...kept, name].join(', ') + ', ');
    setActive(0);
    input.current?.focus();
  };

  return (
    <div className="dropdown tag-input" ref={ref}>
      <input
        ref={input}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && matches.length > 0}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) return setOpen(true);
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setActive((i) => (matches.length ? (i + step + matches.length) % matches.length : 0));
          } else if (e.key === 'Enter' && open && typed && matches[active]) {
            e.preventDefault(); // don't submit the form while choosing a label
            pick(matches[active]);
          } else if (e.key === 'Tab') {
            setOpen(false);
          }
        }}
      />
      {open && matches.length > 0 && (
        <div className="menu left tag-input-menu" role="listbox">
          {matches.map((name, i) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`menu-item ${i === active ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault() /* keep the focus in the input */}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
